import { forwardRef } from 'react';
import { ArrowRight, CalendarDays, MapPin, ShieldCheck, Users, Zap } from 'lucide-react';
import TemplateFrame from './TemplateFrame';
import ClubBrandMark from './ClubBrandMark';
import GuestStyleGridTable from './GuestStyleGridTable';
import PickleballBall from './PickleballBall';
import PickleballPaddle from './PickleballPaddle';
import { AVAILABILITY_IMAGE_LANDSCAPE } from '../../constants/bookingConst';
import { fitClubNameSize } from '../../utils/availabilitySnapshotUtil';

// The lime used for the drawn icons. Lives here rather than in CSS because
// the SVG icons take their colour as a prop.

// Timeslot table sizing (see planGridRows): hourly rows down to 0.8× the
// guest page's size, merged ranges below that.
const GRID_SIZING = { bodyHeight: 560, minHourlyUnit: 0.8, maxUnit: 1.5, maxRowHeight: 80, tableWidth: 1373, maxHeaderUnit: 1.3 };

// The three selling points in the footer strip.
const FEATURES = [
  { key: 'easy', Icon: Zap, label: ['Easy booking'] },
  { key: 'crew', Icon: Users, label: ['Play with', 'your crew'] },
  { key: 'premium', Icon: ShieldCheck, label: ['Premium', 'courts'] },
];

/**
 * A court seen from above, as an outline — the footer's brand tile.
 * @param {object} props
 * @param {string} props.color - stroke colour
 * @returns {JSX.Element}
 */
const CourtOutline = ({ color }) => (
  <svg width="92" height="64" viewBox="0 0 92 64" aria-hidden="true">
    <path d="M10 6 H82 L88 58 H4 Z" fill="none" stroke={color} strokeWidth="3" strokeLinejoin="round" />
    <path d="M46 6 V58 M8 32 H84 M27 6 L24 58 M65 6 L68 58" stroke={color} strokeWidth="2.4" />
  </svg>
);

/**
 * Template — "Court Availability", dark neon design: every hour × every
 * court on a near-black panel in the club's theme colour, and a footer of
 * open-slot count, selling points and a Book Now button.
 * @param {object} props
 * @param {object} props.snapshot - buildAvailabilitySnapshot() output
 * @param {'ranges'|'hourly'} [props.rowMode] - 'hourly' forces one row per hour; 'ranges' merges hours when hourly rows would be too small
 * @param {object} [props.imageSettings] - Availability images settings (font sizes in px, BOOKED colours); see availabilityImageSettingsUtil
 * @returns {JSX.Element}
 */
const FullGridDarkTemplate = forwardRef(function FullGridDarkTemplate({ snapshot, rowMode, imageSettings }, ref) {
  const sport = (snapshot.sportName || 'Pickleball').toUpperCase();
  // The club's Active Button Color, made to read on the dark background.
  const neon = snapshot.palette.accentOnDark;
  const note = snapshot.hasPromo ? 'Promo rates on select hours' : snapshot.hasRateRange ? 'Peak hours apply' : '';

  return (
    <TemplateFrame
      ref={ref}
      width={AVAILABILITY_IMAGE_LANDSCAPE.width}
      height={AVAILABILITY_IMAGE_LANDSCAPE.height}
      className="avg-dark"
      palette={snapshot.palette}
    >
      {/* ---- decoration ---- */}
      <div className="avg-d-court-corner" />
      <div className="avg-d-court-line" />
      <PickleballPaddle width={210} className="avg-d-paddle" />
      <PickleballBall size={92} className="avg-d-ball" />
      <div className="avg-d-stripe avg-d-stripe-1" />
      <div className="avg-d-stripe avg-d-stripe-2" />
      <div className="avg-d-stripe avg-d-stripe-3" />
      <div className="avg-d-stripe avg-d-stripe-4" />
      <div className="avg-d-dots avg-d-dots-right" />
      <div className="avg-d-dots avg-d-dots-left" />

      {/* ---- header ---- */}
      <ClubBrandMark
        snapshot={snapshot}
        iconSize={58}
        iconColor={neon}
        nameSize={fitClubNameSize(snapshot.clubNameMain, 31, 11)}
        className="avg-d-mark"
      />
      <div className="avg-d-divider" />
      <div className="avg-d-meta">
        <span className="avg-d-meta-main">Premium {sport} courts</span>
        <span className="avg-d-meta-sub">Sports &nbsp;/&nbsp; Friends &nbsp;/&nbsp; Good vibes</span>
      </div>
      <div className="avg-d-date">
        <CalendarDays size={34} color={neon} strokeWidth={2} />
        <span className="avg-d-date-text">
          <span className="avg-d-date-day">{snapshot.weekdayUpper}</span>
          <span className="avg-d-date-full">{snapshot.dateCompactUpper}</span>
        </span>
      </div>

      <div className="avg-d-title">
        <span className="avg-d-title-dash" />
        <span className="avg-d-title-text">Court <em>Availability</em></span>
      </div>
      <div className="avg-d-subtitle">Reserve your court. Play your game.</div>

      {/* ---- table ---- */}
      <div className="avg-d-panel">
        <GuestStyleGridTable
          snapshot={snapshot}
          bodyHeight={GRID_SIZING.bodyHeight}
          minHourlyUnit={GRID_SIZING.minHourlyUnit}
          maxUnit={GRID_SIZING.maxUnit}
          maxRowHeight={GRID_SIZING.maxRowHeight}
          tableWidth={GRID_SIZING.tableWidth}
          maxHeaderUnit={GRID_SIZING.maxHeaderUnit}
          rowMode={rowMode}
          imageSettings={imageSettings}
        />
      </div>

      {/* ---- footer ---- */}
      <div className="avg-d-footer">
        <div className="avg-d-brand-tile"><CourtOutline color={neon} /></div>
        <div className="avg-d-slogan">
          <span className="avg-d-slogan-club">{snapshot.clubName}</span>
          <span className="avg-d-slogan-line">More courts.</span>
          <span className="avg-d-slogan-line avg-d-neon">More rallies.</span>
        </div>
        <div className="avg-d-vrule" />
        <div className="avg-d-card avg-d-slots">
          <span className="avg-d-slots-icon"><CalendarDays size={34} color={neon} strokeWidth={1.8} /></span>
          <span className="avg-d-slots-text">
            <span className="avg-d-slots-label">Open slots</span>
            <span className="avg-d-slots-value">
              {snapshot.openCount}<small> of </small>{snapshot.totalCount}
            </span>
          </span>
          {note && <span className="avg-d-slots-note">{note}</span>}
        </div>
        <div className="avg-d-card avg-d-features">
          {FEATURES.map(({ key, Icon, label }) => (
            <div key={key} className="avg-d-feature">
              <Icon size={32} color={neon} strokeWidth={1.8} />
              <span>{label.map(line => <span key={line}>{line}</span>)}</span>
            </div>
          ))}
        </div>
        <div className="avg-d-cta">
          <span className="avg-d-cta-btn">Book now <ArrowRight size={24} strokeWidth={2.6} /></span>
          {snapshot.siteHost && (
            <span className="avg-d-cta-host"><MapPin size={20} color={neon} strokeWidth={2} />{snapshot.siteHost}</span>
          )}
        </div>
      </div>
    </TemplateFrame>
  );
});

export default FullGridDarkTemplate;
